"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { isRtlLocale } from "@/i18n/config";
import {
	FileVideo,
	FileArchive,
	Scissors,
	Combine,
	AudioWaveform,
	ImagePlay,
	Images,
	SearchCode,
	KeyRound,
	Code,
	Globe,
} from "lucide-react";
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";

const navItems = [
	{ key: "converter", path: "/converter", icon: FileVideo },
	{ key: "compressor", path: "/compressor", icon: FileArchive },
	{ key: "trim", path: "/trim", icon: Scissors },
	{ key: "stitch", path: "/stitch", icon: Combine },
	{ key: "audio", path: "/audio", icon: AudioWaveform },
	{ key: "watermark", path: "/watermark", icon: ImagePlay },
	{ key: "metadata", path: "/metadata", icon: SearchCode },
	{ key: "imageConverter", path: "/image-converter", icon: Images },
	{ key: "passwordGenerator", path: "/password-generator", icon: KeyRound },
	{ key: "base64", path: "/base64", icon: Code },
	{ key: "neighbours", path: "/neighbours", icon: Globe },
];

export function AppSidebar() {
	const pathname = usePathname();
	const locale = useLocale();
	const t = useTranslations("nav");

	const isActive = (path: string) => {
		const localePath = `/${locale}${path}`;
		return pathname.startsWith(localePath);
	};

	return (
		<Sidebar side={isRtlLocale(locale) ? "right" : "left"} aria-label="Main navigation">
			<SidebarHeader>
				<Link
					href={`/${locale}`}
					aria-label="FFmpeg.guru - Home"
					className="flex items-center gap-2 font-bold text-lg px-2 py-1"
				>
					<FileVideo aria-hidden="true" className="w-5 h-5" />
					<span>FFmpeg.guru</span>
				</Link>
			</SidebarHeader>
			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupLabel>{t("tools")}</SidebarGroupLabel>
					<SidebarGroupContent>
						<SidebarMenu>
							{navItems.map((item) => {
								const active = isActive(item.path);
								return (
									<SidebarMenuItem key={item.key}>
										<SidebarMenuButton
											asChild
											isActive={active}
											tooltip={t(item.key)}
										>
											<Link
												href={`/${locale}${item.path}`}
												aria-current={active ? "page" : undefined}
											>
												<item.icon aria-hidden="true" />
												<span>{t(item.key)}</span>
											</Link>
										</SidebarMenuButton>
									</SidebarMenuItem>
								);
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
		</Sidebar>
	);
}
